import React, { useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { format, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';
import DashboardSkeleton from '../components/common/DashboardSkeleton';
import { useAuth } from '../hooks/useAuth';
import { useDashboardProfiles } from '../hooks/useDashboardProfiles';
import WidgetGrid from '../widgets/WidgetGrid';
import WidgetPicker from '../widgets/WidgetPicker';
import ProfileSwitcher from '../widgets/ProfileSwitcher';
import AprilscherzBanner from '../components/dashboard/AprilscherzBanner';
import SaisonHinweis from '../components/dashboard/SaisonHinweis';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const today = useMemo(() => startOfDay(new Date()), []);

  const {
    profiles,
    activeProfile,
    loading,
    isEditMode,
    setIsEditMode,
    activateProfile,
    createProfile,
    deleteProfile,
    renameProfile,
    updateLayout,
    addWidget,
    removeWidget,
    setWidgetHeight,
  } = useDashboardProfiles();

  const [pickerOpen, setPickerOpen] = useState(false);

  if (loading) return <DashboardSkeleton />;

  return (
    <Box>
      {/* Feste Begrüßung */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#3dd47e',
              fontSize: { xs: '1.3rem', sm: '1.75rem' },
            }}
          >
            Hallo, {user?.vorname || 'Mitarbeiter'}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: isDark ? '#667788' : '#6b7c8d', fontWeight: 500 }}>
            {format(today, 'EEEE, dd. MMMM yyyy', { locale: de })}
          </Typography>
        </Box>
      </Box>

      <AprilscherzBanner />
      <SaisonHinweis />

      <ProfileSwitcher
        profiles={profiles}
        activeProfile={activeProfile}
        isEditMode={isEditMode}
        onActivate={activateProfile}
        onCreateProfile={(name) => createProfile(name)}
        onDeleteProfile={deleteProfile}
        onRenameProfile={renameProfile}
        onToggleEdit={() => setIsEditMode(!isEditMode)}
        onOpenWidgetPicker={() => setPickerOpen(true)}
      />

      {activeProfile && (
        <WidgetGrid
          widgets={activeProfile.widgets}
          isEditMode={isEditMode}
          onLayoutChange={updateLayout}
          onRemove={removeWidget}
          onPreferredHeight={setWidgetHeight}
        />
      )}

      <WidgetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={(widgetId) => {
          addWidget(widgetId);
          setPickerOpen(false);
        }}
        currentWidgets={activeProfile?.widgets || []}
      />
    </Box>
  );
};

export default DashboardPage;
